import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Radar, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { signalsApi, Signal, SignalSeverity } from '@/lib/signals';

/** Last full calendar month — the default anomaly window. */
function lastMonthRange(): { start: string; end: string } {
  const now = new Date();
  const firstThis = new Date(now.getFullYear(), now.getMonth(), 1);
  const start = new Date(firstThis.getFullYear(), firstThis.getMonth() - 1, 1);
  const end = new Date(firstThis.getFullYear(), firstThis.getMonth(), 0);
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { start: iso(start), end: iso(end) };
}

const DOT: Record<SignalSeverity, string> = {
  high: 'bg-rose-500',
  medium: 'bg-amber-500',
  low: 'bg-ink-300',
};

export function signalKindLabel(t: (k: string, o?: Record<string, unknown>) => string, kind: Signal['kind']): string {
  switch (kind) {
    case 'budget_breach': return t('signals.kind_budget_breach', { defaultValue: 'Budget breach' });
    case 'trend_spike': return t('signals.kind_trend_spike', { defaultValue: 'Trend spike' });
    case 'new_account': return t('signals.kind_new_account', { defaultValue: 'New account' });
    case 'vanished_account': return t('signals.kind_vanished_account', { defaultValue: 'Stopped posting' });
  }
}

function pctText(s: Signal): string {
  if (s.deltaPct === null) return s.kind === 'new_account' ? 'new' : '—';
  const v = Math.round(s.deltaPct * 100);
  return `${v >= 0 ? '+' : ''}${v}%`;
}

/**
 * Dashboard anomaly feed (NUMBER_ONE_PLAN W2-1). Self-fetching over the last
 * full month; renders nothing when there are no signals or on the Free tier
 * (server returns 403 → caught → hidden), so the dashboard is unchanged until
 * anomalies exist. Each row drills to Account search for that code.
 */
export function SignalsPanel({ wsId }: { wsId: string }) {
  const { t } = useTranslation();
  const { start, end } = lastMonthRange();
  const { data, isError } = useQuery({
    queryKey: ['signals-dashboard', wsId, start, end],
    queryFn: () => signalsApi.detect(wsId, { periodStart: start, periodEnd: end }),
    enabled: !!wsId,
    retry: false,
    staleTime: 60_000,
  });

  if (isError || !data || data.signals.length === 0) return null;
  const top = data.signals.slice(0, 6);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-ink-700 inline-flex items-center gap-1.5">
          <Radar className="h-3.5 w-3.5 text-emerald-700" />
          {t('signals.dashboard_title', { defaultValue: 'Signals' })}
          <span className="text-[11px] font-normal text-muted-foreground">
            {t('signals.count', { defaultValue: '{{n}} detected', n: data.signals.length })}
          </span>
        </h2>
        <Link to={`/app/workspace/${wsId}/signals`} className="text-[12px] font-semibold text-emerald-700 hover:text-emerald-800">
          {t('signals.view_all', { defaultValue: 'View all →' })}
        </Link>
      </div>
      <div className="rounded-md border border-border bg-card divide-y divide-border">
        {top.map((s) => (
          <Link
            key={s.id}
            to={`/app/workspace/${wsId}/account-search?q=${encodeURIComponent(s.accountCode)}`}
            className="flex items-center gap-3 px-3.5 py-2 hover:bg-ink-50/60"
          >
            <span className={`h-2 w-2 rounded-full shrink-0 ${DOT[s.severity]}`} aria-hidden />
            <span className="num text-[12px] text-ink-700 shrink-0 w-20 truncate" title={s.accountCode}>{s.accountCode}</span>
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground shrink-0 w-28 truncate">
              {signalKindLabel(t, s.kind)}
            </span>
            <span className="flex-1" />
            <span className={`inline-flex items-center gap-0.5 num text-[12px] font-semibold ${s.direction === 'up' ? 'text-emerald-700' : 'text-rose-600'}`}>
              {s.direction === 'up' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
              {pctText(s)}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
